import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, MessageCircle } from "lucide-react";
import { getBag, setQty, removeFromBag, checkoutUrl, bagSubtotal } from "@/lib/bag";
import { waLink } from "@/config/site";

const BagDrawer = () => {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);

    useEffect(() => {
        const sync = () => setItems(getBag());
        const opener = () => {
            sync();
            setOpen(true);
        };
        sync();
        window.addEventListener("bsk:bag", sync);
        window.addEventListener("bsk:bag:open", opener);
        return () => {
            window.removeEventListener("bsk:bag", sync);
            window.removeEventListener("bsk:bag:open", opener);
        };
    }, []);

    const subtotal = bagSubtotal();
    const waItems = items.map((i) => `• ${i.name} × ${i.qty}`).join("\n");

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        className="fixed inset-0 z-[93] bg-obsidian/70 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setOpen(false)}
                    />
                    <motion.aside
                        className="fixed top-0 right-0 bottom-0 z-[94] w-full max-w-md bg-charcoal border-l border-gold/25 flex flex-col"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.4, ease: [0.22, 0.6, 0.24, 1] }}
                        data-testid="bag-drawer"
                    >
                        <div className="flex items-center justify-between px-7 py-6 border-b border-gold/15">
                            <h2 className="font-serif text-2xl flex items-center gap-3">
                                <ShoppingBag size={20} strokeWidth={1.5} className="text-gold" /> Your Bag
                            </h2>
                            <button onClick={() => setOpen(false)} data-testid="bag-close-btn" aria-label="Close bag" className="text-sand hover:text-gold transition-colors">
                                <X size={20} strokeWidth={1.5} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">
                            {items.length === 0 && (
                                <div className="text-center pt-20" data-testid="bag-empty">
                                    <ShoppingBag size={30} strokeWidth={1} className="text-gold mx-auto" />
                                    <p className="font-serif italic text-2xl text-sand mt-6">Your bag is empty.</p>
                                    <Link
                                        to="/collections"
                                        onClick={() => setOpen(false)}
                                        data-testid="bag-browse-btn"
                                        className="inline-block mt-8 bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-8 py-3.5 hover:bg-gold-bright transition-colors"
                                    >
                                        Explore Collections
                                    </Link>
                                </div>
                            )}
                            {items.map((i) => (
                                <div key={i.slug} className="flex gap-4 border-b border-gold/10 pb-6" data-testid={`bag-item-${i.slug}`}>
                                    <Link to={`/product/${i.slug}`} onClick={() => setOpen(false)} className="w-20 aspect-[3/4] shrink-0 overflow-hidden border border-gold/15">
                                        <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif text-lg text-ivory leading-snug truncate">{i.name}</p>
                                        <p className="text-[11px] tracking-[0.2em] uppercase text-gold mt-1">{i.price}</p>
                                        <div className="flex items-center gap-3 mt-3">
                                            <button onClick={() => setQty(i.slug, i.qty - 1)} data-testid={`bag-minus-${i.slug}`} aria-label="Decrease" className="w-7 h-7 border border-gold/25 flex items-center justify-center text-sand hover:text-gold hover:border-gold transition-colors">
                                                <Minus size={12} />
                                            </button>
                                            <span className="text-sm w-4 text-center" data-testid={`bag-qty-${i.slug}`}>{i.qty}</span>
                                            <button onClick={() => setQty(i.slug, i.qty + 1)} data-testid={`bag-plus-${i.slug}`} aria-label="Increase" className="w-7 h-7 border border-gold/25 flex items-center justify-center text-sand hover:text-gold hover:border-gold transition-colors">
                                                <Plus size={12} />
                                            </button>
                                        </div>
                                    </div>
                                    <button onClick={() => removeFromBag(i.slug)} data-testid={`bag-remove-${i.slug}`} aria-label="Remove" className="text-sand/60 hover:text-gold transition-colors self-start">
                                        <Trash2 size={15} strokeWidth={1.5} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {items.length > 0 && (
                            <div className="border-t border-gold/15 px-7 py-6 space-y-4">
                                <div className="flex justify-between items-baseline">
                                    <span className="text-[11px] tracking-[0.25em] uppercase text-sand">Subtotal</span>
                                    <span className="font-serif text-2xl gold-text" data-testid="bag-subtotal">
                                        ₹ {subtotal.toLocaleString("en-IN")}
                                    </span>
                                </div>
                                <a
                                    href={checkoutUrl()}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="bag-checkout-btn"
                                    className="block w-full bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase py-4 text-center hover:bg-gold-bright transition-colors"
                                >
                                    Secure Checkout →
                                </a>
                                <a
                                    href={waLink(`Hello BSK Jewellers, I would like to enquire about:\n${waItems}`)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="bag-whatsapp-btn"
                                    className="flex items-center justify-center gap-2 w-full border border-gold/50 text-gold text-[11px] tracking-[0.25em] uppercase py-4 hover:bg-gold hover:text-obsidian transition-colors"
                                >
                                    <MessageCircle size={14} /> Enquire on WhatsApp Instead
                                </a>
                                <p className="text-[10px] text-sand/60 text-center">Checkout is completed securely on Shopify. Taxes and making charges confirmed at checkout.</p>
                            </div>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
};

export default BagDrawer;
